import { Component, OnInit } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { tap, catchError, of, Observable } from 'rxjs';
import { ComponentBase } from 'src/app/components/core/_infrastructure/classes/componentBase';
import { ExceptionManager } from 'src/app/components/core/_infrastructure/classes/handlers/exceptionManager';
import { AuthService } from 'src/app/components/core/_infrastructure/classes/services/auth.service';
import { NotificationService } from 'src/app/components/core/_infrastructure/classes/services/notification.service';
import { NavMenuService } from 'src/app/components/core/components/navmenu/navmenu.service';
import { InspeccionUiService } from '../../inspeccion-ui.service';
import { UnidadInspeccionService } from '../../unidad-inspeccion.service';
import { ResultadoInspeccionSelected, TwInspeccionCamViewModel, TwInspeccionUiCamViewModel } from '../../inspeccion.types';
import { Tc } from 'src/app/components/core/_infrastructure/types/common';
import { EnumCodigosTwTcAcciones } from 'src/app/components/_importacion/solicitudes/types/enum';
import { ForeignKeyValidator } from 'src/app/components/core/_infrastructure/classes/foreignKeyValidator';
import {
    InspeccionPeriodoCamViewModel,
    ListaInspeccionPeriodoViewModel,
} from 'src/app/components/_mantenimiento/campanias/periodos/inspecciones/inspecciones.types';
import { CampaniasGlobalService } from '../../../services/global-campanias.service';
import { TwUnidadInspeccionViewModelExtended } from '../../unidad-inspeccion-datosbasicos.types';
import { EnumIdsTcTipoSolicitudCam } from '../../../types/enum';
import { ListaPlazosPeriodoCamViewModel } from 'src/app/components/_mantenimiento/campanias/periodos/plazos/plazos.types';
import { PeriodoPlazoService } from 'src/app/components/_mantenimiento/campanias/periodos/plazos/plazos.service';

@Component({
    selector: 'app-inspeccion-ui-wrapper-edit',
    templateUrl: './inspeccion-ui-wrapper-edit.component.html',
    styleUrls: ['./inspeccion-ui-wrapper-edit.component.scss'],
})
export class InspeccionUiWrapperEditComponent extends ComponentBase implements OnInit {
    visibilitySpinnerGuardando = false;
    formGroupHeader: FormGroup;
    formGroupInspeccion: FormGroup;

    unidadInspeccion: TwUnidadInspeccionViewModelExtended;
    inspeccion: TwInspeccionUiCamViewModel;
    listaInspeccionPeriodo: InspeccionPeriodoCamViewModel[];
    resultadosInspeccion: Tc[];
    listaPlazosPeriodo: ListaPlazosPeriodoCamViewModel;
    fechaInicioExportacionVigente: Date | null;
    habilitadaExportacion = false;

    private promiseInspeccion: Promise<boolean>;

    private inspeccionUIDetailUrl = '/campanias/unidadinspeccion/inspecciones/';
    private successMessage = 'Inspección guardada correctamente';

    private activatedRoute$ = this.activatedRoute.paramMap.subscribe(params => {
        const idUi: any = params.get('idUi');
        if (idUi) {
            this.getUnidadInspeccion(idUi);
        }

        const idInspeccion: any = params.get('id');
        if (idInspeccion) {
            this.promiseInspeccion = this.getInspeccion(idInspeccion);
        }
    });

    title = { plural: 'Inspecciones', singular: 'Inspección' };

    constructor(
        private fb: FormBuilder,
        private activatedRoute: ActivatedRoute,
        private router: Router,
        private unidadInspeccionService: UnidadInspeccionService,
        private periodoPlazoService: PeriodoPlazoService,
        private inspeccionUIService: InspeccionUiService,
        private globalCampaniasService: CampaniasGlobalService,
        private navMenuService: NavMenuService,
        protected authService: AuthService,
        protected notificationService: NotificationService,
        protected exceptionHandler: ExceptionManager,
    ) {
        super(authService, notificationService, exceptionHandler);
    }

    ngOnInit(): void {
        this.preparePresentation();
    }

    private preparePresentation(): void {
        this.loadLists();
        this.buildForm();
    }

    private buildForm(): void {
        this.formGroupHeader = this.fb.group({
            Campania: [null],
            Periodo: [null],
            DescripcionComunidad: [null],
            EntidadAuditora: [null],
            TipoRecinto: [null], // Tipo de Solicitud
            Operador: [null],
        });

        this.formGroupInspeccion = this.fb.group({
            IdPeriodoInspeccionCam: [null, Validators.compose([Validators.required, ForeignKeyValidator.cannotBeNullOrZero])],
            NombreInspeccion: [{ value: null, disabled: true }], // readonly
            FechaCaducidad: [null], // Será obligatoria si se selecciona un tipo de inspección (del periodo) que tenga el campo "TieneFechafin" a true.
            Observaciones: [null],
            FechaInspeccion: [{ value: null, disabled: true }], // readonly
            IdResultadoInspeccionCam: [null, Validators.compose([Validators.required, ForeignKeyValidator.cannotBeNullOrZero])],
            Inspector: [{ value: null, disabled: true }], // readonly
            // Not visible but needed
            Id: [null],
            IdUnidadInspeccionCam: [null],
            IdUsuario: [false],
            IdUsuarioModificacion: [null],
            FechaModificacion: [null],
            IdUsuarioBorrado: [null],
            FechaBorrado: [null],
        });

        const subgrupo = this.fb.group({
            inspeccion: this.formGroupInspeccion,
        });

        this.form = subgrupo;
    }

    private loadLists(): void {
        // cargar lista de resultados de inspección
        this.getListaResultadosInspeccion();
    }

    /**
     * Se obtiene la lista de tipos de resultado para la inspección, que se utilizará en el desplegable.
     */
    private getListaResultadosInspeccion(): void {
        this.resultadosInspeccion = [];
        this.globalCampaniasService
            .getListaResultadosInspeccionCam()
            .pipe(
                tap((results: Tc[]) => {
                    this.resultadosInspeccion = results;
                }),
                catchError(error => {
                    this.catchServerError(error);
                    return of(null);
                }),
            )
            .subscribe();
    }

    /**
     * Se obtiene la lista de tipos de inspecciones del periodo.
     * A partir de ella se construye la lista que se utilizará en el desplegable.
     * @param periodoId: number
     * @return void
     */
    private getListaTiposInspeccionesPeriodo(periodoId: number): void {
        if (!periodoId) {
            return;
        }
        this.listaInspeccionPeriodo = [];

        this.globalCampaniasService
            .getListaInspeccionesDePeriodo(periodoId)
            .pipe(
                tap((results: ListaInspeccionPeriodoViewModel) => {
                    if (results) {
                        this.listaInspeccionPeriodo = results?.InspeccionPeriodoCollection;
                        // Llegados a éste punto, ya se debería haber cargado el objeto Inspeccion (en caso que estemos editando)
                        this.promiseInspeccion?.then(() => {
                            if (this.inspeccion) {
                                this.onTipoInspeccionSelected(this.inspeccion.IdPeriodoInspeccionCam);
                                const resultadoInspeccionSelected = new ResultadoInspeccionSelected();
                                resultadoInspeccionSelected.idResultado = this.inspeccion.IdResultadoInspeccionCam!;
                                resultadoInspeccionSelected.idPeriodoInspeccionCam = this.inspeccion.IdPeriodoInspeccionCam;
                            }
                        });
                    }
                }),
                catchError(error => {
                    this.catchServerError(error);
                    return of(null);
                }),
            )
            .subscribe();
    }

    /**
     * Se cargan los datos de la unidad de inspección y sus recintos "PI-A-Ed" asociados
     * Una vez obtenida, establece el valor para la propiedad readonly a partir de la información del usuario y los permisos de edición.
     * Después invoca al servicio de obtención de inspecciones de la U.I.
     * @param id number
     */
    private getUnidadInspeccion(id: number): void {
        this.unidadInspeccionService
            .getUI(id)
            .pipe(
                tap((udInspeccion: TwUnidadInspeccionViewModelExtended) => {
                    this.onUnidadInspeccionLoaded(udInspeccion);
                }),
                catchError(error => {
                    this.catchServerError(error);
                    this.formGroupInspeccion.disable();
                    return of(null);
                }),
            )
            .subscribe();
    }

    private onUnidadInspeccionLoaded(udInspeccion: TwUnidadInspeccionViewModelExtended): void {
        // Una UI existente deberá tener al menos un recinto "PI-A-Ed".
        if (!udInspeccion?.FirstRecinto) {
            // No debería pasar por aquí, porque cuando se eliminan todos los recintos "PI-A-Ed" de una UI, se elimina además la UI
            return;
        }
        this.unidadInspeccion = udInspeccion;

        // Cargamos la lista de tipos de inspecciones del periodo
        if (this.unidadInspeccion.FirstRecinto?.IdPeriodo) {
            this.getListaTiposInspeccionesPeriodo(this.unidadInspeccion.FirstRecinto?.IdPeriodo);
        } else {
            // @author : por aquí no debería pasar
            console.error('Primer Recinto de Unidad de Inspección sin periodo', this.unidadInspeccion?.IdUi);
        }

        this.navMenuService.updateMenuServiceForUdInspeccion(
            udInspeccion.IdUi!,
            udInspeccion.IdEstadoUi!,
            udInspeccion.FirstRecinto.IdTipoSolicitudCam || 0,
            udInspeccion.PeriodoCertificaKgs,
        );

        this.getPlazos(this.unidadInspeccion.FirstRecinto.IdPeriodo); // Cargamos los plazos del periodo.
    }

    /**
     * Obtenemos los plazos del periodo, para saber si hay alguno que ya haya empezado.
     * Si hay alguno empezado se deshabilitado el campo ControlProduccionExportacion.
     * @param idPeriodo number
     */
    private getPlazos(idPeriodo: number): void {
        this.periodoPlazoService
            .getListaPlazosDePeriodo(idPeriodo)
            .pipe(
                tap((results: ListaPlazosPeriodoCamViewModel) => {
                    this.listaPlazosPeriodo = results;
                    this.fechaInicioExportacionVigente = this.getFechaInicioExportacion(this.unidadInspeccion.FirstRecinto.IdTipoSolicitudCam!);
                }),
                catchError(error => {
                    this.catchServerError(error);
                    return of(null);
                }),
            )
            .subscribe();
    }

    /**
     * Cargamos los datos de la inspección de la U.I.
     * @param idInspeccion number
     */
    private getInspeccion(idInspeccion: number): Promise<boolean> {
        return new Promise((resolve, reject) => {
            this.inspeccionUIService
                .get(idInspeccion)
                .pipe(
                    tap((inspeccion: TwInspeccionCamViewModel) => {
                        this.inspeccion = inspeccion as TwInspeccionUiCamViewModel;
                        this.formGroupInspeccion.disable();
                        resolve(true);
                    }),
                    catchError(error => {
                        this.catchServerError(error);
                        this.formGroupInspeccion.disable();
                        reject(false);
                        return of(null);
                    }),
                )
                .subscribe();
        });
    }

    onTipoInspeccionSelected(idTipoInspeccion: number): void {
        if (!idTipoInspeccion) {
            this.formGroupInspeccion.controls['FechaCaducidad'].removeValidators(ForeignKeyValidator.cannotBeNullOrZero);
        }
        const tipoInspeccionSeleccionado = this.listaInspeccionPeriodo?.find(s => s.Id == idTipoInspeccion);
        if (tipoInspeccionSeleccionado) {
            // Hacemos obligatorio el campo "FechaCaducidad", si "TieneFechaFin" es true
            if (tipoInspeccionSeleccionado.TieneFechaFin) {
                this.formGroupInspeccion.controls['FechaCaducidad'].addValidators(ForeignKeyValidator.cannotBeNullOrZero);
            } else {
                this.formGroupInspeccion.controls['FechaCaducidad'].removeValidators(ForeignKeyValidator.cannotBeNullOrZero);
            }
        }
        // Comprobamos si había seleccionado previamente el resultado
        const IdResultadoInspeccionCam = this.formGroupInspeccion.controls['IdResultadoInspeccionCam'].value;
        const IdPeriodoInspeccionCam = this.formGroupInspeccion.controls['IdPeriodoInspeccionCam'].value;
        const resInspeccionSelected = new ResultadoInspeccionSelected();
        resInspeccionSelected.idResultado = IdResultadoInspeccionCam;
        resInspeccionSelected.idPeriodoInspeccionCam = IdPeriodoInspeccionCam;
    }

    /**
     * Se crea/guarda la inspección de la U.I. y si tiene un corte, también
     */
    onClickSave(): void {
        const inspeccionValida = this.validarInspeccion();
        if (!inspeccionValida) {
            return;
        }

        this.doSaveInspeccion()
            .pipe(
                tap((idInspeccionCreada: number) => {
                    this.visibilitySpinnerGuardando = false;
                    this.addSuccessMessage(this.successMessage);
                    this.onBack();
                }),
                catchError(error => {
                    this.visibilitySpinnerGuardando = false;
                    this.catchServerError(error);
                    if (error?.error instanceof Array) {
                        this.addErrorMessage(error?.error[0].message);
                    }
                    return of(null);
                }),
            )
            .subscribe();
    }

    private validarInspeccion(): boolean {
        const fechaCaducidad = this.formGroupInspeccion.controls['FechaCaducidad'].value;
        if (fechaCaducidad) {
            const inspeccionValida = this.checkFechaCaducidadYPlazoVigente(new Date(fechaCaducidad));
            return inspeccionValida;
        }

        return true;
    }

    /**
     * Comprobamos si la fecha de caducidad del código de autorización no es posterior a la fecha de inicio de exportación del tipo de solicitud correspondiente
     * Se comprueba que la fecha de caducidad no sea anterior a la fecha-inicio-exportacion
     * @returns void
     */
    private checkFechaCaducidadYPlazoVigente(fechaCaducidad: Date): boolean {
        if (fechaCaducidad > new Date(this.listaPlazosPeriodo.PeriodoFechaFin!)) {
            this.addErrorMessage('La fecha de caducidad no puede ser superior a la fecha fin del periodo');
            this.setCustomError('FechaCaducidadFueraDePeriodo', 'inspeccion.FechaCaducidad');
            return false;
        }
        this.removeCustomError('FechaCaducidadFueraDePeriodo', 'inspeccion.FechaCaducidad');

        if (this.habilitadaExportacion && this.fechaInicioExportacionVigente) {
            if (fechaCaducidad < this.fechaInicioExportacionVigente!) {
                this.addErrorMessage('La fecha de caducidad no puede ser anterior al plazo de exportación vigente');
                this.setCustomError('FechaCaducidadFueraPlazo', 'inspeccion.FechaCaducidad');
                return false;
            }
        }

        this.removeCustomError('FechaCaducidadFueraPlazo', 'inspeccion.FechaCaducidad');
        return true;
    }

    /**
     * Guardamos la fecha del primer plazo de exportación más reciente, en función del tipo de solicitud.
     * @param tipoSolicitud: number
     * @returns Date | null
     */
    private getFechaInicioExportacion(tipoSolicitud: number): Date | null {
        let fechaInicioExportacion = null;

        for (let i = 0; i < this.listaPlazosPeriodo.PlazoPeriodoCollection.length; i++) {
            const plazoPeriodo = this.listaPlazosPeriodo.PlazoPeriodoCollection[i];
            if (plazoPeriodo) {
                switch (tipoSolicitud) {
                    case EnumIdsTcTipoSolicitudCam.ID_CONFECCION:
                        this.habilitadaExportacion = plazoPeriodo.HabilitadaExportacionAlmacenes;
                        if (plazoPeriodo.FechaInicioExportacionConfeccion) {
                            const fiEC = new Date(plazoPeriodo.FechaInicioExportacionConfeccion);
                            if (fechaInicioExportacion === null) {
                                fechaInicioExportacion = fiEC;
                                break;
                            }
                            if (fiEC < fechaInicioExportacion) {
                                fechaInicioExportacion = fiEC;
                                break;
                            }
                        }
                        break;
                    case EnumIdsTcTipoSolicitudCam.ID_EMPRESA_DESHIDRATACION:
                        this.habilitadaExportacion = plazoPeriodo.HabilitadaExportacionEmpDeshidratadoras;
                        if (plazoPeriodo.FechaInicioExportacionEmpDeshidratadoras) {
                            const fiEED = new Date(plazoPeriodo.FechaInicioExportacionEmpDeshidratadoras);
                            if (fechaInicioExportacion === null) {
                                fechaInicioExportacion = fiEED;
                                break;
                            }
                            if (fiEED < fechaInicioExportacion) {
                                fechaInicioExportacion = fiEED;
                                break;
                            }
                        }
                        break;
                    case EnumIdsTcTipoSolicitudCam.ID_INVERNADERO:
                        this.habilitadaExportacion = plazoPeriodo.HabilitadaExportacionInvernaderos;
                        if (plazoPeriodo.FechaInicioExportacionInvernadero) {
                            const fiEI = new Date(plazoPeriodo.FechaInicioExportacionInvernadero);
                            if (fechaInicioExportacion === null) {
                                fechaInicioExportacion = fiEI;
                                break;
                            }
                            if (fiEI < fechaInicioExportacion) {
                                fechaInicioExportacion = fiEI;
                                break;
                            }
                        }
                        break;
                    case EnumIdsTcTipoSolicitudCam.ID_PARCELA:
                        this.habilitadaExportacion = plazoPeriodo.HabilitadaExportacionParcelas;
                        if (plazoPeriodo.FechaInicioExportacionParcela) {
                            const fiEP = new Date(plazoPeriodo.FechaInicioExportacionParcela);
                            if (fechaInicioExportacion === null) {
                                fechaInicioExportacion = fiEP;
                                break;
                            }
                            if (fiEP < fechaInicioExportacion) {
                                fechaInicioExportacion = fiEP;
                                break;
                            }
                        }
                        break;
                }
            }
        }
        return fechaInicioExportacion;
    }

    private doSaveInspeccion(): Observable<any> {
        const modelInspeccion = { ...this.formGroupInspeccion.getRawValue() };
        this.submitted = true;
        this.visibilitySpinnerGuardando = true;
        this.inspeccion = { ...(modelInspeccion as TwInspeccionUiCamViewModel) };
        if (this.inspeccion) {
            if (!this.inspeccion.IdUnidadInspeccionCam) {
                this.inspeccion.IdUnidadInspeccionCam = this.unidadInspeccion.IdUi!;
            }
        }
        return this.inspeccionUIService.insertOrUpdateUI(this.inspeccion);
    }

    onBack(): void {
        this.router.navigate([this.inspeccionUIDetailUrl, this.unidadInspeccion?.IdUi]);
    }

    /**
     * Este método indica si se pueden guardar inspecciones para la unidad de inspección.
     * Si el objeto unidadInspeccion no se ha cargado, no se le habilita el permiso.
     * Además se comprueba que el usuario (por perfil) tenga los permisos necesarios.
     * @return boolean
     */
    get hasSavingAccess(): boolean {
        if (!this.unidadInspeccion) return false;

        const hasAddingAccess =
            this.loggedInUser?.getAction(
                EnumCodigosTwTcAcciones.ADD_INSPECCION_UI,
                this.unidadInspeccion.IdEstadoUi,
                this.unidadInspeccion.FirstRecinto.IdTipoSolicitudCam,
            ).Editable || false;

        const hasEditingAccess =
            this.loggedInUser?.getAction(
                EnumCodigosTwTcAcciones.EDIT_INSPECCION_UI,
                this.unidadInspeccion.IdEstadoUi,
                this.unidadInspeccion.FirstRecinto.IdTipoSolicitudCam,
            ).Editable || false;

        return (
            (hasAddingAccess || hasEditingAccess) &&
            (!this.inspeccion || this.inspeccion.Id === 0) &&
            this.unidadInspeccion?.FirstRecinto?.PeriodoEnPlazoVigente
        );
    }

    get hasViewAccess(): boolean {
        if (!this.inspeccion || this.inspeccion.Id === 0 || !this.unidadInspeccion) return false;

        const hasViewingAccess =
            this.loggedInUser?.getAction(
                EnumCodigosTwTcAcciones.CONS_INSPECCION_UI,
                this.unidadInspeccion.IdEstadoUi,
                this.unidadInspeccion.FirstRecinto.IdTipoSolicitudCam,
            ).Editable || false;

        return hasViewingAccess;
    }

    get actionNotAllowed(): boolean {
        return this.visibilitySpinnerGuardando || this.formInvalid || !this.unidadInspeccion?.FirstRecinto?.PeriodoEnPlazoVigente;
    }

    private get formInvalid(): boolean {
        return !this.formGroupInspeccion.valid || !this.formGroupInspeccion.dirty || this.formGroupInspeccion.pristine;
    }

    get readonly(): boolean {
        return (!this.hasSavingAccess && this.hasViewAccess) || (this.inspeccion !== null && this.inspeccion !== undefined && this.inspeccion.Id !== 0);
    }
}
